import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Snackbar, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select,
  FormControl, RadioGroup, FormControlLabel, Radio, Divider,
} from '@mui/material';
import {
  PlayArrowRounded, CheckRounded, PersonAddRounded, EditRounded, LockRounded, MedicalServicesRounded, RefreshRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useTokens } from '../../hooks/useTokens';
import {
  Token, TokenStatus, TokenFilter, TOKEN_STATUS_META, PENDING_STATUSES, tokenCustomerName,
} from '../../api/tokenTypes';
import { QueueProvider } from '../../api/queueTypes';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../constants/countries';
import { useNavigate } from 'react-router-dom';

interface Props {
  queueId: string;
  date: string;          // YYYY-MM-DD
  readOnly: boolean;     // token ACTIONS (serve/skip/cancel/complete) — today + active only
  readOnlyReason?: string;
  /** Add walk-in is allowed for today AND future dates (only past/inactive blocked),
   *  mirroring mobile — so it's separate from action read-only. Controls visibility. */
  canAddWalkIn: boolean;
  /** Day's max tokens (from schedule) — used to disable walk-in when the day is full. */
  maxTokensForDay?: number | null;
  /** If set, the walk-in button is shown but disabled with this reason (closed day / holiday). */
  walkInDisabledReason?: string;
  /** Reports the selected-day token stats so the page's capacity bar reflects the chosen date. */
  onStats?: (stats: { booked: number; total: number }) => void;
  /** Providers assigned to THIS queue. `provider.id` is the team_member id, which
   *  is what PATCH /tokens/:id/provider expects (NOT user_id — see plan O4). */
  providers: QueueProvider[];
  /** Whether the Consultation action is available for this queue's business
   *  (category consultation enabled AND prescription plan/trial). */
  consultationEnabled: boolean;
}

const FILTERS: { key: TokenFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'skipped', label: 'Skipped' },
];

const StatusChip: React.FC<{ status: TokenStatus }> = ({ status }) => {
  const m = TOKEN_STATUS_META[status];
  return <Chip label={m.label} size="small" sx={{ bgcolor: `${m.color}1F`, color: m.color, fontWeight: 600, height: 22 }} />;
};

const TokensSection: React.FC<Props> = ({ queueId, date, readOnly, readOnlyReason, canAddWalkIn, maxTokensForDay, walkInDisabledReason, providers, onStats, consultationEnabled }) => {
  const { getTokens, createWalkIn, updateStatus, assignProvider, completeToken } = useTokens();
  const navigate = useNavigate();
  // keep onStats in a ref so refetch doesn't depend on a new callback each render
  const onStatsRef = useRef(onStats);
  useEffect(() => { onStatsRef.current = onStats; });

  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TokenFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // dialogs
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<Token | null>(null);
  const [editTarget, setEditTarget] = useState<Token | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<Token | null>(null);
  const [serveTarget, setServeTarget] = useState<Token | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'serve' | 'skip' | 'cancel'; token: Token } | null>(null);

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const res = await getTokens(queueId, date);
      setTokens(res.tokens);
      onStatsRef.current?.({ booked: res.tokens.filter((t) => t.status !== 'cancelled').length, total: res.tokens.length });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load tokens.');
    } finally {
      setLoading(false);
    }
  }, [getTokens, queueId, date]);

  useEffect(() => { setLoading(true); refetch(); }, [refetch]);

  const counts = useMemo(() => {
    const c = { all: tokens.length, pending: 0, completed: 0, skipped: 0 };
    tokens.forEach((t) => {
      const g = TOKEN_STATUS_META[t.status].group;
      if (g === 'pending') c.pending++;
      else if (g === 'completed') c.completed++;
      else if (g === 'skipped') c.skipped++;
    });
    return c;
  }, [tokens]);

  // Queue is "full" when non-cancelled tokens reach the day's max (mirrors mobile).
  const isFull = useMemo(() => {
    if (maxTokensForDay == null) return false;
    return tokens.filter((t) => t.status !== 'cancelled').length >= maxTokensForDay;
  }, [tokens, maxTokensForDay]);
  const walkInBlock = walkInDisabledReason || (isFull ? 'Queue is full for this day.' : '');

  const visible = useMemo(() => {
    let list: Token[];
    if (filter === 'all') list = tokens;
    else if (filter === 'pending') list = tokens.filter((t) => PENDING_STATUSES.includes(t.status));
    else list = tokens.filter((t) => TOKEN_STATUS_META[t.status].group === filter);
    // Order: serving (in_progress) on top, completed last, everything else in between (stable sort).
    const weight = (s: TokenStatus) => (s === 'in_progress' ? 0 : s === 'completed' ? 2 : 1);
    return [...list].sort((a, b) => weight(a.status) - weight(b.status));
  }, [tokens, filter]);

  const act = useCallback(async (id: string, fn: () => Promise<any>, msg: string) => {
    setBusyId(id);
    try { await fn(); await refetch(); setToast(msg); }
    catch (e: any) { setToast(e?.response?.data?.message || 'Action failed'); }
    finally { setBusyId(null); }
  }, [refetch]);

  // Serve flow — mirrors mobile: >1 provider → ask who serves first; exactly one
  // provider → auto-assign them and serve; none → just serve.
  const performServe = (t: Token) => {
    if (providers.length > 1) {
      setServeTarget(t);
    } else if (providers.length === 1) {
      const pid = providers[0].id;
      act(t.id, async () => { await assignProvider(t.id, pid); await updateStatus(t.id, 'in_progress'); }, `Now serving #${t.token_number}`);
    } else {
      act(t.id, () => updateStatus(t.id, 'in_progress'), `Now serving #${t.token_number}`);
    }
  };
  const serveWithProvider = (t: Token, providerId: string) => {
    setServeTarget(null);
    act(t.id, async () => { await assignProvider(t.id, providerId); await updateStatus(t.id, 'in_progress'); }, `Now serving #${t.token_number}`);
  };

  // Every direct token action is confirmed first (mirrors mobile).
  const runConfirm = () => {
    if (!confirmAction) return;
    const { type, token: t } = confirmAction;
    setConfirmAction(null);
    if (type === 'serve') performServe(t);
    else if (type === 'skip') act(t.id, () => updateStatus(t.id, 'skipped'), `Token #${t.token_number} skipped`);
    else if (type === 'cancel') act(t.id, () => updateStatus(t.id, 'cancelled'), `Token #${t.token_number} cancelled`);
  };
  // Wording matches the mobile app's confirmation modals exactly.
  const CONFIRM_META = {
    serve: { title: 'Serve Token?', subtitle: (n: number) => `Start serving Token #${n}?`, ok: 'Serve', danger: false },
    skip: { title: 'Skip Token?', subtitle: (n: number) => `Are you sure you want to skip Token #${n}?`, ok: 'Skip', danger: true },
    cancel: { title: 'Cancel Token?', subtitle: (n: number) => `Are you sure you want to cancel Token #${n}? This cannot be undone.`, ok: 'Cancel Token', danger: true },
  } as const;

  // shared style so every row action button is the same height
  const aSx = { height: 34, whiteSpace: 'nowrap' as const };

  const rowActions = (t: Token) => {
    if (readOnly) return null;
    const busy = busyId === t.id;
    if (['booked', 'waiting'].includes(t.status)) {
      return (
        <>
          <Button size="small" variant="contained" color="success" disableElevation disabled={busy} startIcon={<PlayArrowRounded />} onClick={() => setConfirmAction({ type: 'serve', token: t })} sx={aSx}>Serve</Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => setConfirmAction({ type: 'skip', token: t })} sx={aSx}>Skip</Button>
        </>
      );
    }
    if (t.status === 'in_progress') {
      return (
        <>
          <Button size="small" variant="contained" disableElevation disabled={busy} startIcon={<CheckRounded />} onClick={() => setCompleteTarget(t)} sx={aSx}>Complete</Button>
          {consultationEnabled && (
            <Button size="small" variant="outlined" disabled={busy} startIcon={<MedicalServicesRounded />} onClick={() => navigate(`/queues/${queueId}/consultation/${t.id}`)} sx={aSx}>Consultation</Button>
          )}
          <Button size="small" variant="text" color="error" disabled={busy} onClick={() => setConfirmAction({ type: 'cancel', token: t })} sx={aSx}>Cancel</Button>
        </>
      );
    }
    // completed / skipped / cancelled / no_show
    return <Button size="small" variant="text" startIcon={<EditRounded />} onClick={() => setEditTarget(t)} sx={aSx}>Edit status</Button>;
  };

  const numTile = (t: Token) => {
    const m = TOKEN_STATUS_META[t.status];
    const serving = t.status === 'in_progress';
    return (
      <Box sx={{ width: 48, height: 48, borderRadius: '12px', flexShrink: 0, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 17, bgcolor: serving ? m.color : `${m.color}1F`, color: serving ? '#fff' : m.color }}>
        {t.token_number}
      </Box>
    );
  };

  return (
    <Box>
      {readOnly && (
        <Alert icon={<LockRounded />} severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
          {readOnlyReason || 'Token actions are available only for today on an active queue.'}
        </Alert>
      )}

      {/* Filter tabs + walk-in — uniform 40px height, vertically centered */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <Button key={f.key} onClick={() => setFilter(f.key)} disableElevation
                sx={{ height: 40, borderRadius: 99, px: 2, bgcolor: active ? colors.gray900 : 'transparent', color: active ? '#fff' : colors.textSecondary, border: `1px solid ${active ? colors.gray900 : colors.border}`, '&:hover': { bgcolor: active ? colors.gray900 : colors.gray50 } }}>
                {f.label}
                <Box component="span" sx={{ ml: 0.75, px: 0.75, borderRadius: 99, fontSize: 12, bgcolor: active ? 'rgba(255,255,255,.2)' : colors.gray100 }}>{counts[f.key]}</Box>
              </Button>
            );
          })}
          {/* Refresh — right after the Skipped chip */}
          <Tooltip title="Refresh tokens">
            <span>
              <IconButton onClick={async () => { setRefreshing(true); await refetch(); setRefreshing(false); }} disabled={refreshing}
                sx={{ width: 40, height: 40, border: `1px solid ${colors.border}`, borderRadius: '10px' }}>
                {refreshing ? <CircularProgress size={18} /> : <RefreshRounded />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {canAddWalkIn && (
          <Tooltip title={walkInBlock}>
            <span>
              <Button variant="contained" disableElevation startIcon={<PersonAddRounded />} disabled={!!walkInBlock} onClick={() => setWalkInOpen(true)} sx={{ height: 40, whiteSpace: 'nowrap' }}>Add walk-in</Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: colors.textSecondary }}>
          <Typography>No tokens {filter !== 'all' ? `in "${filter}"` : ''} for this day.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visible.map((t) => {
            const serving = t.status === 'in_progress';
            return (
              <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 1.75, border: `1px solid ${serving ? colors.success : colors.border}`, borderRadius: '12px', bgcolor: serving ? '#ECFDF5' : colors.backgroundPaper }}>
                {numTile(t)}
                <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setDetailsTarget(t)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700 }}>{tokenCustomerName(t)}</Typography>
                    <StatusChip status={t.status} />
                    <Chip size="small" label={t.token_type === 'walk_in' ? 'Walk-in' : 'Pre-booked'} sx={{ height: 20, fontSize: 10, bgcolor: colors.gray100, color: colors.gray600 }} />
                  </Box>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    {t.customer?.phone_number || '—'}
                    {PENDING_STATUSES.includes(t.status) && t.estimated_wait_mins != null ? ` • Est. wait ${t.estimated_wait_mins} min` : ''}
                    {t.provider?.display_name ? ` • ${t.provider.display_name}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>{rowActions(t)}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Walk-in dialog */}
      {walkInOpen && <WalkInDialog onClose={() => setWalkInOpen(false)} onSubmit={async (payload) => {
        try { await createWalkIn(queueId, { ...payload, scheduled_date: date }); setWalkInOpen(false); await refetch(); setToast('Walk-in token added'); }
        catch (e: any) { setToast(e?.response?.data?.message || 'Failed to add walk-in'); }
      }} />}

      {/* Complete dialog */}
      {completeTarget && <CompleteDialog token={completeTarget} onClose={() => setCompleteTarget(null)} onSubmit={async (payload) => {
        const t = completeTarget; setCompleteTarget(null);
        await act(t.id, () => completeToken(t.id, payload), `Token #${t.token_number} completed`);
      }} />}

      {/* Edit-status dialog */}
      {editTarget && <EditStatusDialog token={editTarget} onClose={() => setEditTarget(null)} onSubmit={(status) => {
        const t = editTarget; setEditTarget(null);
        // Serving from edit goes through performServe (prompts provider when >1).
        if (status === 'in_progress') performServe(t);
        else act(t.id, () => updateStatus(t.id, status), 'Status updated');
      }} />}

      {/* Details dialog */}
      {detailsTarget && <DetailsDialog token={detailsTarget} providers={providers} readOnly={readOnly}
        onClose={() => setDetailsTarget(null)}
        onAssign={async (providerId) => {
          const t = detailsTarget; setDetailsTarget(null);
          await act(t.id, () => assignProvider(t.id, providerId), 'Provider assigned');
        }} />}

      {/* Serve: choose provider first (queue has >1 provider) */}
      {serveTarget && <ServeProviderDialog token={serveTarget} providers={providers}
        onClose={() => setServeTarget(null)}
        onConfirm={(providerId) => serveWithProvider(serveTarget, providerId)} />}

      {/* Confirm every direct token action (mirrors mobile) */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} fullWidth maxWidth="xs">
        {confirmAction && (() => {
          const meta = CONFIRM_META[confirmAction.type];
          const t = confirmAction.token;
          return (
            <>
              <DialogTitle>{meta.title}</DialogTitle>
              <DialogContent>
                <Typography color="text.secondary">{meta.subtitle(t.token_number)}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
                <Button variant="contained" color={meta.danger ? 'error' : 'primary'} onClick={runConfirm}>{meta.ok}</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

/* ---------------- dialogs ---------------- */

const WalkInDialog: React.FC<{ onClose: () => void; onSubmit: (p: { customer_name: string; customer_phone: string; country_code: string }) => void }> = ({ onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(DEFAULT_COUNTRY.dialCode);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add walk-in customer</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth label="Customer name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl sx={{ width: 130, flexShrink: 0 }}>
            <Select value={code} onChange={(e) => setCode(e.target.value)} size="small" sx={{ height: 56 }}
              renderValue={(val) => { const c = COUNTRIES.find((x) => x.dialCode === val); return <span>{c?.flag} {val}</span>; }}>
              {COUNTRIES.map((c) => <MenuItem key={c.id} value={c.dialCode}>{c.flag} {c.name} ({c.dialCode})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim()} onClick={() => onSubmit({ customer_name: name.trim(), customer_phone: phone, country_code: code })}>Add token</Button>
      </DialogActions>
    </Dialog>
  );
};

const CompleteDialog: React.FC<{ token: Token; onClose: () => void; onSubmit: (p: { payment_amount?: number; note?: string }) => void }> = ({ token, onClose, onSubmit }) => {
  const [amount, setAmount] = useState(token.payment_amount?.toString() || '');
  const [note, setNote] = useState(token.note || '');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Complete token #{token.token_number}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>{tokenCustomerName(token)}</Typography>
        <TextField fullWidth type="number" label="Payment amount (optional)" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ mb: 2 }} />
        <TextField fullWidth multiline minRows={2} label="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<CheckRounded />} onClick={() => onSubmit({ payment_amount: amount ? Number(amount) : undefined, note: note || undefined })}>Mark complete</Button>
      </DialogActions>
    </Dialog>
  );
};

// Only values the status endpoint accepts (waiting | in_progress | skipped |
// cancelled). "completed" uses the /complete endpoint; "no_show" is not settable
// here. Mirrors mobile's edit-status options (Waiting / Serve / Cancel).
const EDIT_OPTIONS: { value: TokenStatus; label: string }[] = [
  { value: 'waiting', label: 'Re-open (Waiting)' },
  { value: 'in_progress', label: 'Serve' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'cancelled', label: 'Cancel token' },
];

const EditStatusDialog: React.FC<{ token: Token; onClose: () => void; onSubmit: (s: TokenStatus) => void }> = ({ token, onClose, onSubmit }) => {
  const [status, setStatus] = useState<TokenStatus>(token.status);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Update status — #{token.token_number}</DialogTitle>
      <DialogContent>
        <RadioGroup value={status} onChange={(e) => setStatus(e.target.value as TokenStatus)}>
          {EDIT_OPTIONS.map((o) => <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />)}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(status)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const ServeProviderDialog: React.FC<{ token: Token; providers: QueueProvider[]; onClose: () => void; onConfirm: (providerId: string) => void }> = ({ token, providers, onClose, onConfirm }) => {
  const [providerId, setProviderId] = useState(token.provider?.id || '');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign provider — #{token.token_number}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          This queue has multiple providers. Choose who will serve {tokenCustomerName(token)}, then start.
        </Typography>
        <FormControl fullWidth>
          <Select value={providerId} displayEmpty onChange={(e) => setProviderId(e.target.value)}>
            <MenuItem value="" disabled>Select provider</MenuItem>
            {providers.map((p) => <MenuItem key={p.id} value={p.id}>{p.display_name || 'Provider'}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" startIcon={<PlayArrowRounded />} disabled={!providerId} onClick={() => onConfirm(providerId)}>Assign &amp; serve</Button>
      </DialogActions>
    </Dialog>
  );
};

const DetailsDialog: React.FC<{ token: Token; providers: QueueProvider[]; readOnly: boolean; onClose: () => void; onAssign: (providerId: string) => void }> = ({ token, providers, readOnly, onClose, onAssign }) => {
  // value is the team_member id (provider.id) — what the assign endpoint expects
  const [providerId, setProviderId] = useState(token.provider?.id || '');
  const row = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${colors.border}` }}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>{value || '—'}</Typography>
    </Box>
  );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Token #{token.token_number}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1 }}><StatusChip status={token.status} /></Box>
        {row('Customer', tokenCustomerName(token))}
        {row('Phone', token.customer?.phone_number)}
        {row('Type', token.token_type === 'walk_in' ? 'Walk-in' : 'Pre-booked')}
        {row('Provider', token.provider?.display_name)}
        {row('Total visits', token.total_visits)}
        {row('Est. wait', token.estimated_wait_mins != null ? `${token.estimated_wait_mins} min` : '—')}
        {row('Payment', token.payment_amount != null ? `₹${token.payment_amount}` : '—')}
        {row('Notes', token.note)}

        {!readOnly && providers.length > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Assign provider</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl fullWidth size="small">
                <Select value={providerId} displayEmpty onChange={(e) => setProviderId(e.target.value)}>
                  <MenuItem value="" disabled>Select provider</MenuItem>
                  {providers.map((p) => <MenuItem key={p.id} value={p.id}>{p.display_name || 'Provider'}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" disabled={!providerId} onClick={() => onAssign(providerId)}>Assign</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" fullWidth onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokensSection;
